import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Plus, Trash2, RefreshCw, CheckCircle, XCircle, Wifi } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SumUpSettings() {
  const [settings, setSettings] = useState({
    apiKeyConfigured: false,
    affiliateKeyConfigured: false,
    appId: '',
    merchantCode: '',
    enableSumupTerminalPayments: false,
    terminals: []
  });
  const [newApiKey, setNewApiKey] = useState('');
  const [newAffiliateKey, setNewAffiliateKey] = useState('');
  const [newTerminal, setNewTerminal] = useState({ name: '', terminalId: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/sumup/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load SumUp settings:', error);
      toast.error('Failed to load SumUp settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!settings.merchantCode?.trim()) {
      toast.error('Merchant Code is required');
      return;
    }
    if (!settings.appId?.trim()) {
      toast.error('App ID is required');
      return;
    }
    if (settings.terminals.length === 0) {
      toast.error('At least one terminal is required');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Trim all values before sending
      const payload = {
        apiKey: newApiKey?.trim() || undefined,
        affiliateKey: newAffiliateKey?.trim() || undefined,
        appId: settings.appId?.trim(),
        merchantCode: settings.merchantCode?.trim(),
        enableSumupTerminalPayments: settings.enableSumupTerminalPayments,
        terminals: settings.terminals.map(t => ({
          id: t.id,
          name: t.name?.trim(),
          terminalId: t.terminalId?.trim()
        }))
      };
      
      await axios.put(`${API}/superadmin/sumup/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SumUp settings saved successfully');
      setNewApiKey('');
      setNewAffiliateKey('');
      loadSettings();
    } catch (error) {
      console.error('Failed to save SumUp settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/superadmin/sumup/test-connection`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Connection test failed'
      });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleAddTerminal = () => {
    const trimmedName = newTerminal.name?.trim();
    const trimmedId = newTerminal.terminalId?.trim();
    
    if (!trimmedName || !trimmedId) {
      toast.error('Please enter both terminal name and ID');
      return;
    }
    
    // Validate terminal ID format (should start with rdr_)
    if (!trimmedId.startsWith('rdr_')) {
      toast.error('Terminal ID should start with "rdr_"');
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      terminals: [
        ...prev.terminals,
        {
          id: Date.now().toString(),
          name: trimmedName,
          terminalId: trimmedId
        }
      ]
    }));
    setNewTerminal({ name: '', terminalId: '' });
  };

  const handleRemoveTerminal = (id) => {
    setSettings(prev => ({
      ...prev,
      terminals: prev.terminals.filter(t => t.id !== id)
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-gray-500">Loading SumUp settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="sumup-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Payments - SumUp Solo
        </CardTitle>
        <CardDescription>
          Configure SumUp Solo terminal for spectator payments during check-in
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <Label className="text-base font-semibold">Enable Terminal Payments</Label>
            <p className="text-sm text-gray-600">
              Allow spectator payments via SumUp Solo during registration
            </p>
          </div>
          <Switch
            checked={settings.enableSumupTerminalPayments}
            onCheckedChange={(checked) => setSettings(prev => ({
              ...prev,
              enableSumupTerminalPayments: checked
            }))}
            data-testid="sumup-enable-toggle"
          />
        </div>

        {/* API Credentials */}
        <div className="space-y-4 p-4 border rounded-lg">
          <h3 className="font-semibold text-lg">API Credentials</h3>
          <p className="text-sm text-gray-600">
            Credentials are stored securely and never exposed in the frontend.
            Get your keys from the <a href="https://me.sumup.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">SumUp Dashboard</a>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>API Key</Label>
              <div className="flex items-center gap-2 mt-1">
                {settings.apiKeyConfigured ? (
                  <Badge className="bg-green-600">Configured</Badge>
                ) : (
                  <Badge variant="outline" className="text-orange-600 border-orange-600">Not Set</Badge>
                )}
              </div>
              <Input
                type="password"
                value={newApiKey}
                onChange={(e) => setNewApiKey(e.target.value)}
                placeholder={settings.apiKeyConfigured ? "Enter new key to update..." : "sk_live_..."}
                className="mt-2"
                data-testid="sumup-api-key-input"
              />
            </div>

            <div>
              <Label>Affiliate Key</Label>
              <div className="flex items-center gap-2 mt-1">
                {settings.affiliateKeyConfigured ? (
                  <Badge className="bg-green-600">Configured</Badge>
                ) : (
                  <Badge variant="outline" className="text-orange-600 border-orange-600">Not Set</Badge>
                )}
              </div>
              <Input
                type="password"
                value={newAffiliateKey}
                onChange={(e) => setNewAffiliateKey(e.target.value)}
                placeholder={settings.affiliateKeyConfigured ? "Enter new key to update..." : "Enter affiliate key..."}
                className="mt-2"
                data-testid="sumup-affiliate-key-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>App ID</Label>
              <Input
                value={settings.appId}
                onChange={(e) => setSettings(prev => ({ ...prev, appId: e.target.value }))}
                placeholder="com.example.app"
                className="mt-1"
                data-testid="sumup-app-id-input"
              />
            </div>

            <div>
              <Label>Merchant Code</Label>
              <Input
                value={settings.merchantCode}
                onChange={(e) => setSettings(prev => ({ ...prev, merchantCode: e.target.value }))}
                placeholder="YOUR_MERCHANT_CODE"
                className="mt-1"
                data-testid="sumup-merchant-code-input"
              />
            </div>
          </div>
        </div>

        {/* Test Connection */}
        <div className="p-4 border rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Test Connection</h3>
              <p className="text-sm text-gray-600">Verify your SumUp credentials and reader connectivity</p>
            </div>
            <Button
              onClick={handleTestConnection}
              disabled={testing || !settings.apiKeyConfigured}
              variant="outline"
              className="border-black"
              data-testid="sumup-test-connection-btn"
            >
              {testing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wifi className="w-4 h-4 mr-2" />
              )}
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
          </div>

          {testResult && (
            <div className={`mt-4 p-3 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <span className={testResult.success ? 'text-green-700' : 'text-red-700'}>
                  {testResult.message}
                </span>
              </div>
              {testResult.success && testResult.readersFound > 0 && (
                <div className="mt-3">
                  <p className="text-sm text-green-700 font-semibold mb-2">
                    Found {testResult.readersFound} reader(s) — click to add:
                  </p>
                  <div className="space-y-2">
                    {testResult.readers?.map((reader, idx) => {
                      const alreadyAdded = settings.terminals.some(t => t.terminalId === reader.id);
                      return (
                        <div key={idx} className="flex items-center justify-between bg-white border border-green-200 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{reader.name || `Reader ${idx + 1}`}</p>
                            <p className="text-xs text-gray-500 font-mono">ID: {reader.id}</p>
                            <p className="text-xs text-gray-400">{reader.status || 'Unknown status'}</p>
                          </div>
                          {alreadyAdded ? (
                            <Badge className="bg-green-600 text-xs">Added</Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-600 text-green-700 hover:bg-green-50 text-xs"
                              onClick={() => {
                                setSettings(prev => ({
                                  ...prev,
                                  terminals: [...prev.terminals, {
                                    id: Date.now().toString(),
                                    name: reader.name || `Reader ${idx + 1}`,
                                    terminalId: reader.id
                                  }]
                                }));
                                toast.success(`Reader "${reader.name || reader.id}" added`);
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Reader
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-green-600 mt-2">Remember to click "Save SumUp Settings" after adding readers.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Terminals */}
        <div className="p-4 border rounded-lg">
          <h3 className="font-semibold text-lg mb-2">Card Readers (Terminals)</h3>
          <p className="text-sm text-gray-600 mb-4">
            Configure multiple terminals for different check-in desks. Terminal IDs are found in your SumUp dashboard.
          </p>

          {settings.terminals.length > 0 && (
            <div className="space-y-2 mb-4">
              {settings.terminals.map((terminal) => (
                <div
                  key={terminal.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{terminal.name}</p>
                    <p className="text-sm text-gray-500">ID: {terminal.terminalId}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveTerminal(terminal.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={newTerminal.name}
              onChange={(e) => setNewTerminal(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Terminal name (e.g., Front Desk)"
              className="flex-1"
              data-testid="sumup-terminal-name-input"
            />
            <Input
              value={newTerminal.terminalId}
              onChange={(e) => setNewTerminal(prev => ({ ...prev, terminalId: e.target.value }))}
              placeholder="Terminal ID"
              className="flex-1"
              data-testid="sumup-terminal-id-input"
            />
            <Button
              onClick={handleAddTerminal}
              variant="outline"
              className="border-black"
              data-testid="sumup-add-terminal-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-black hover:bg-gray-800"
            data-testid="sumup-save-settings-btn"
          >
            {saving ? 'Saving...' : 'Save SumUp Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
